import React from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { usePresence } from "../../../hooks/usePresence";
import { OVERLAY_EXIT_MS } from "../../../design-system/tokens/overlayMotion";
import { IconButton } from "../../atoms/IconButton";
import {
  Body,
  Description,
  Dialog,
  CloseSlot,
  Footer,
  FooterEnd,
  FooterStart,
  Header,
  HeaderCenter,
  HeaderEnd,
  HeaderText,
  Kicker,
  KickerNode,
  Root,
  Scrim,
  Title,
  Toolbar,
} from "./DialogShell.styles";
import type { DialogShellProps } from "./DialogShell.types";

/** The close button's glyph, in the shape `IconButton` takes. */
const CloseGlyph: React.FC<{ size?: number }> = ({ size }) => <X size={size} aria-hidden="true" />;

/** A modal dialog's frame, drawn over the page in a portal. Renders nothing while closed. */
export const DialogShell: React.FC<DialogShellProps> = ({
  open,
  size = "md",
  width,
  headerDivider = true,
  radius = "xl",
  chrome = "default",
  kicker,
  title,
  description,
  titleId,
  descriptionId,
  closeLabel,
  dialogRef,
  titleRef,
  onKeyDown,
  onClose,
  hideClose = false,
  toolbar,
  headerCenter,
  headerEnd,
  flush = false,
  largeTitle = false,
  bareHeader = false,
  filledClose = false,
  tintedFooter = false,
  children,
  footerStart,
  footerEnd,
}) => {
  const { mounted } = usePresence(open, OVERLAY_EXIT_MS);
  const closing = !open;
  return mounted
    ? createPortal(
        <Root $chrome={chrome}>
          <Scrim $chrome={chrome} $closing={closing} data-testid="dialog-scrim" onMouseDown={onClose} />
          <Dialog
            ref={dialogRef}
            $size={size}
            $width={width}
            $radius={radius}
            $chrome={chrome}
            $closing={closing}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            aria-describedby={description && descriptionId ? descriptionId : undefined}
            data-size={size}
            data-chrome={chrome}
            onKeyDown={onKeyDown}
          >
            <Header $divider={headerDivider} $centered={Boolean(headerCenter || headerEnd)} $chrome={chrome} $bare={bareHeader}>
              <HeaderText>
                {kicker ? typeof kicker === "string" ? <Kicker>{kicker}</Kicker> : <KickerNode>{kicker}</KickerNode> : null}
                <Title $chrome={chrome} $large={largeTitle} id={titleId} ref={titleRef} tabIndex={-1}>
                  {title}
                </Title>
                {description ? <Description id={descriptionId}>{description}</Description> : null}
              </HeaderText>
              {headerCenter ? <HeaderCenter>{headerCenter}</HeaderCenter> : null}
              {headerEnd ? <HeaderEnd>{headerEnd}</HeaderEnd> : null}
              {hideClose ? null : filledClose ? (
                <CloseSlot>
                  <IconButton icon={CloseGlyph} aria-label={closeLabel} onClick={onClose} />
                </CloseSlot>
              ) : (
                <IconButton icon={CloseGlyph} aria-label={closeLabel} onClick={onClose} />
              )}
            </Header>
            {toolbar ? <Toolbar>{toolbar}</Toolbar> : null}
            <Body $flush={flush}>{children}</Body>
            {footerStart || footerEnd ? (
              <Footer $tinted={tintedFooter}>
                {footerStart ? <FooterStart>{footerStart}</FooterStart> : null}
                {footerEnd ? <FooterEnd>{footerEnd}</FooterEnd> : null}
              </Footer>
            ) : null}
          </Dialog>
        </Root>,
        document.body,
      )
    : null;
};

export default DialogShell;
